/**
 * @file index.tsx
 *
 * @fileoverview Renders the google analytics 4 tag in the page.
 */

import React, { useContext, useEffect, useRef } from 'react';
import Script from 'next/script';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import Router from 'next/router';
import { concatArrayItems } from '~/jobiqo/utils/mapper';
import { ANONYMOUS_USER } from '~/jobiqo/lib/auth';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const usePageReloadIfG4AConsentGotRemoved = (currentG4AConsent) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise G4A does not get excluded.
  const previousG4AConsent = useRef<boolean>(null);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousG4AConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.GoogleAnalytics4,
    );
  });

  // Compare if consent has changed from true to false.
  if (previousG4AConsent.current && !currentG4AConsent) {
    Router.reload();
  }
};

export const GoogleAnalytics = () => {
  const testing = process.env.testing;
  const staging = process.env.staging;

  const userContext: UserContextInterface = useContext(UserContext);
  const { APP } = useFrontendConfig();
  const GA4_MEASUREMENT_IDS = APP.GA4_MEASUREMENT_IDS;

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.GoogleAnalytics4,
  );

  usePageReloadIfG4AConsentGotRemoved(isUserConsentNeededAndGiven);

  return isUserConsentNeededAndGiven &&
    GA4_MEASUREMENT_IDS &&
    !testing &&
    !staging ? (
    <>
      <Script
        id="ga4-script"
        async={true}
        src={`https://www.googletagmanager.com/gtag/js`}
      />

      <Script id="ga4-config" strategy="lazyOnload">
        {`window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                ${GA4_MEASUREMENT_IDS.map((measurementId) => {
                  const configParams: {
                    current_user_id?: number;
                    current_user_role?: string;
                  } = {};

                  configParams.current_user_id = userContext.user?.id;
                  configParams.current_user_role =
                    concatArrayItems(userContext.user?.roles, 'targetId') ??
                    ANONYMOUS_USER;
                  return `gtag('config', '${measurementId}',${JSON.stringify(
                    configParams,
                  )});
                  window.gtag_fully_loaded = true;
                  `;
                }).join('')}
              `}
      </Script>
    </>
  ) : null;
};
export default GoogleAnalytics;
